import React, { useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import { Button, Portal, SpeedDial, SpeedDialAction, SpeedDialIcon, SwipeableDrawer, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import BrandingWatermarkOutlinedIcon from '@mui/icons-material/BrandingWatermarkOutlined';
import BookmarkAddedOutlinedIcon from '@mui/icons-material/BookmarkAddedOutlined';
import type { StudioPreferencesStore, StudioSettings } from '../constants';
import BackgroundControl from './Sidebar/controls/BackgroundControl';
import FormatControl from './Sidebar/controls/FormatControl';
import LanguageControl from './Sidebar/controls/LanguageControl';
import LogoWatermarkControl from './Sidebar/controls/LogoWatermarkControl';
import ProfileControl from './Sidebar/controls/ProfileControl';
import TemplateControl from './Sidebar/controls/TemplateControl';
import PreferencesControl from './Sidebar/controls/PreferencesControl';

type ToolId = 'template' | 'background' | 'watermark' | 'language' | 'format' | 'profile' | 'preferences';

const tools: Array<{ id: ToolId; labelId: string; Icon: React.ElementType }> = [
  { id: 'preferences', labelId: 'hot-topic-studio-defaults', Icon: BookmarkAddedOutlinedIcon },
  { id: 'template', labelId: 'hot-topic-studio-template', Icon: LayersOutlinedIcon },
  { id: 'background', labelId: 'hot-topic-studio-background', Icon: PaletteOutlinedIcon },
  { id: 'watermark', labelId: 'hot-topic-studio-watermark', Icon: BrandingWatermarkOutlinedIcon },
  { id: 'language', labelId: 'hot-topic-studio-language', Icon: TranslateIcon },
  { id: 'format', labelId: 'hot-topic-studio-format', Icon: AspectRatioIcon },
  { id: 'profile', labelId: 'hot-topic-studio-profile', Icon: PersonOutlinedIcon }
];

interface MobileToolBarProps {
  intl: any;
  settings: StudioSettings;
  onChangeSettings: (partial: Partial<StudioSettings>) => void;
  preferencesStore: StudioPreferencesStore;
  selectedSlot: number;
  slotName: string;
  onSelectSlot: (slotIndex: number) => void;
  onChangeSlotName: (name: string) => void;
  onRenameSlot: () => void;
  onSavePreferences: () => void;
  onClearPreferences: () => void;
  hideProfile?: boolean;
}

const MobileToolBar: React.FC<MobileToolBarProps> = ({
  intl,
  settings,
  onChangeSettings,
  preferencesStore,
  selectedSlot,
  slotName,
  onSelectSlot,
  onChangeSlotName,
  onRenameSlot,
  onSavePreferences,
  onClearPreferences,
  hideProfile = false
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTool, setActiveTool] = useState<ToolId | null>(null);
  const visibleTools = useMemo(() => (hideProfile ? tools.filter(tool => tool.id !== 'profile') : tools), [
    hideProfile
  ]);
  const selectedTool = useMemo(() => visibleTools.find(tool => tool.id === activeTool), [activeTool, visibleTools]);

  const control = {
    preferences: (
      <PreferencesControl
        intl={intl}
        preferencesStore={preferencesStore}
        selectedSlot={selectedSlot}
        slotName={slotName}
        onSelectSlot={onSelectSlot}
        onChangeSlotName={onChangeSlotName}
        onRenameSlot={onRenameSlot}
        onSave={onSavePreferences}
        onClear={onClearPreferences}
      />
    ),
    template: (
      <TemplateControl template={settings.template} onChangeTemplate={template => onChangeSettings({ template })} />
    ),
    background: (
      <BackgroundControl
        themeColor={settings.themeColor}
        onChangeThemeColor={themeColor => onChangeSettings({ themeColor })}
      />
    ),
    watermark: (
      <LogoWatermarkControl
        logo={settings.watermarkLogo}
        onChangeLogo={watermarkLogo => onChangeSettings({ watermarkLogo })}
      />
    ),
    language: <LanguageControl locale={settings.locale} onChangeLocale={locale => onChangeSettings({ locale })} />,
    format: <FormatControl format={settings.format} onChangeFormat={format => onChangeSettings({ format })} />,
    profile: (
      <ProfileControl
        profileName={settings.profileName}
        onChangeProfileName={profileName => onChangeSettings({ profileName })}
      />
    )
  }[activeTool as ToolId];

  const openTool = (toolId: ToolId) => {
    setMenuOpen(false);
    setActiveTool(toolId);
  };

  return (
    <>
      <Portal>
        <div className="studio-mobile-tools-fab">
          <SpeedDial
            open={menuOpen}
            ariaLabel={intl.formatMessage({ id: 'hot-topic-studio-customize' })}
            icon={<SpeedDialIcon icon={<TuneRoundedIcon />} openIcon={<CloseRoundedIcon />} />}
            onOpen={() => setMenuOpen(true)}
            onClose={() => setMenuOpen(false)}
            direction="up"
            FabProps={{
              color: 'primary',
              size: 'medium'
            }}
          >
            {visibleTools.map(({ id, labelId, Icon }) => (
              <SpeedDialAction
                key={id}
                icon={<Icon />}
                slotProps={{
                  tooltip: {
                    title: intl.formatMessage({ id: labelId }),
                    open: true
                  }
                }}
                onClick={() => openTool(id)}
              />
            ))}
          </SpeedDial>
        </div>
      </Portal>
      <SwipeableDrawer
        anchor="bottom"
        open={Boolean(activeTool)}
        onOpen={() => {}}
        onClose={() => setActiveTool(null)}
        disableSwipeToOpen
        slotProps={{
          paper: {
            className: 'studio-mobile-tool-sheet'
          }
        }}
      >
        <div className="studio-mobile-sheet-handle" />
        <div className="studio-mobile-sheet-header">
          <Typography variant="subtitle1">
            {selectedTool ? intl.formatMessage({ id: selectedTool.labelId }) : ''}
          </Typography>
          <Button color="primary" onClick={() => setActiveTool(null)}>
            {intl.formatMessage({ id: 'hot-topic-studio-done' })}
          </Button>
        </div>
        <div className="studio-mobile-sheet-content">{control}</div>
      </SwipeableDrawer>
    </>
  );
};

export default injectIntl(MobileToolBar);
